import Papa from 'papaparse';
import XLSX from 'xlsx-js-style';
import {
  generateTimestampedFilename,
  formatHeader,
  findTimestampColumns,
  formatTimestamps,
} from './commonFunctions';

const THIN_BORDER = {
  top: { style: 'thin', color: { rgb: '000000' } },
  bottom: { style: 'thin', color: { rgb: '000000' } },
  left: { style: 'thin', color: { rgb: '000000' } },
  right: { style: 'thin', color: { rgb: '000000' } },
};

const applyValueMapping = (cleanVal, colKey, row, valueMappings) => {
  const mapping = valueMappings[colKey];
  if (!mapping) return cleanVal;
  if (typeof mapping === 'function') {
    const result = mapping(cleanVal, row);
    return result === null || typeof result === 'undefined'
      ? cleanVal
      : String(result);
  }
  if (Object.prototype.hasOwnProperty.call(mapping, cleanVal)) {
    return mapping[cleanVal];
  }
  if (Object.prototype.hasOwnProperty.call(mapping, '__default__')) {
    return mapping.__default__;
  }
  return cleanVal;
};

const applyDateFormat = (
  worksheet,
  finalData,
  finalTimestampCols,
  dateFormat
) => {
  const totalRows = finalData.length;
  finalTimestampCols.forEach(c => {
    for (let r = 1; r < totalRows; r++) {
      if (!(finalData[r]?.[c] instanceof Date)) continue;
      const ref = XLSX.utils.encode_cell({ r, c });
      const cell = worksheet[ref];
      if (!cell) continue;
      cell.t = 'd';
      cell.z = dateFormat;
    }
  });
};

const styleHeaderRow = (worksheet, headers) => {
  if (!headers?.length) return;
  const headerBg = { patternType: 'solid', fgColor: { rgb: 'FFE8F4FF' } };
  for (let col = 0; col < headers.length; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
    if (!worksheet[cellRef]) continue;
    worksheet[cellRef].s = {
      ...(worksheet[cellRef].s || {}),
      font: { ...(worksheet[cellRef].s?.font || {}), bold: true },
      fill: { ...(worksheet[cellRef].s?.fill || {}), ...headerBg },
      border: THIN_BORDER,
    };
  }
};

const setColumnWidths = (worksheet, finalData, headers, dateFormat) => {
  try {
    const colCount = (headers && headers.length) || finalData[0]?.length || 0;
    const maxColWidths = new Array(colCount).fill(0);
    finalData.forEach(row => {
      for (let c = 0; c < colCount; c++) {
        const raw = row?.[c];
        const cell =
          raw instanceof Date
            ? dateFormat
            : raw !== undefined && raw !== null
              ? String(raw)
              : '';
        const longestLine = cell
          .split('\n')
          .reduce((m, l) => Math.max(m, l.length), 0);
        if (longestLine > maxColWidths[c]) maxColWidths[c] = longestLine;
      }
    });
    worksheet['!cols'] = maxColWidths.map(w => ({
      wch: Math.min(Math.max(Math.ceil(w + 2), 8), 60),
    }));
  } catch {
    // ignore column width calculation failures
  }
};

const applyBorderStyle = (worksheet, finalData) => {
  try {
    const rowCount = finalData.length || 0;
    const colCountAll = finalData[0]?.length || 0;
    for (let r = 0; r < rowCount; r++) {
      for (let c = 0; c < colCountAll; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        if (!worksheet[cellRef]) continue;
        worksheet[cellRef].s = {
          ...(worksheet[cellRef].s || {}),
          border: worksheet[cellRef].s?.border || THIN_BORDER,
        };
      }
    }
  } catch {
    // ignore styling failures
  }
};

/**
 * Generic paginated CSV -> styled XLSX downloader.
 *
 * @param {Object}   params
 * @param {Function} params.fetchPage      async (pageNumber, pageSize) =>
 *                                          ({ csvData, nextPageNumber })
 * @param {Object}   params.formatOptions  selectedColumns / headerOptions / etc.
 * @param {string}   params.fileBaseName   base name (no extension)
 * @param {number}   [params.pageSize=500]
 * @param {boolean}  [params.timestampAsDate=false]  true -> Excel date cells
 * @param {string}   [params.sheetName='Data']
 * @param {string}   [params.dateFormat='dd-mm-yyyy h:mm AM/PM']
 * @returns {Promise<{ rowCount: number, fileName: string }>}
 *          rowCount === 0 means nothing was downloaded.
 */
export const downloadDataAsExcel = async ({
  fetchPage,
  formatOptions = {},
  fileBaseName,
  pageSize = 500,
  timestampAsDate = false,
  sheetName = 'Data',
  dateFormat = 'dd-mm-yyyy h:mm AM/PM',
}) => {
  const opts = { ...formatOptions };
  let allData = [];
  let headers = null;
  let originalHeaders = null;
  let keepIndices = null;
  let finalTimestampCols = [];
  let mergeSpecs = null;

  const applyMerges = row => {
    if (!mergeSpecs || !mergeSpecs.length) return row;
    const additions = mergeSpecs.map(({ idxs, separator, compute }) => {
      const parts = idxs.map(i => (i >= 0 ? String(row[i] ?? '').trim() : ''));
      if (compute) return compute(...parts);
      return parts.every(Boolean) ? parts.join(separator) : '';
    });
    return [...row, ...additions];
  };

  const processChunk = (parsed, isFirstChunk) => {
    if (parsed.data.length === 0) return;

    if (isFirstChunk) {
      originalHeaders = parsed.data[0];

      mergeSpecs = (opts.mergeColumns || []).map(
        ({ key, from, separator = ', ', compute }) => ({
          key,
          idxs: from.map(h => originalHeaders.indexOf(h)),
          separator,
          compute,
        })
      );
      mergeSpecs.forEach(spec => originalHeaders.push(spec.key));

      if (Array.isArray(opts.selectedColumns) && opts.selectedColumns.length) {
        keepIndices = opts.selectedColumns
          .map(name => originalHeaders.indexOf(name))
          .filter(idx => idx !== -1);
      } else {
        keepIndices = null;
      }

      const baseHeaders = opts.formatHeaders
        ? originalHeaders.map(header =>
            formatHeader(header, opts.headerOptions || {})
          )
        : originalHeaders;

      headers =
        keepIndices && keepIndices.length
          ? keepIndices.map(i => baseHeaders[i])
          : baseHeaders;

      opts.timestampColumns = findTimestampColumns(originalHeaders, opts);

      parsed.data.slice(1).forEach(row => allData.push(applyMerges(row)));
    } else {
      parsed.data.forEach(row => allData.push(applyMerges(row)));
    }
  };

  const formatAllData = () => {
    let rows = [...allData].map(r => r);
    if (keepIndices && keepIndices.length) {
      rows = rows.map(row =>
        keepIndices.map(i => (i < row.length ? row[i] : ''))
      );
    }

    let timestampColsForFormatting = opts.timestampColumns || [];
    if (
      keepIndices &&
      keepIndices.length &&
      timestampColsForFormatting.length
    ) {
      timestampColsForFormatting = timestampColsForFormatting
        .map(origIdx => keepIndices.indexOf(origIdx))
        .filter(idx => idx !== -1);
    }
    finalTimestampCols = timestampColsForFormatting;

    const finalColumnKeys =
      keepIndices && keepIndices.length
        ? keepIndices.map(i => originalHeaders[i])
        : originalHeaders;

    let formattedData = rows;
    if (opts.enableFormatting) {
      if (timestampColsForFormatting?.length) {
        formattedData = formattedData.map(row =>
          formatTimestamps(row, timestampColsForFormatting, {
            asDate: timestampAsDate,
          })
        );
      }
      if (opts.customRowFormatter) {
        formattedData = formattedData.map((row, index) =>
          opts.customRowFormatter(row, index)
        );
      }
    }

    const valueMappings = opts.valueMappings || {};

    const toCellValue = val =>
      val === null || typeof val === 'undefined' ? '' : String(val).trim();

    formattedData = formattedData.map(row =>
      row.map((cell, colIdx) => {
        // leave Date cells untouched when in date mode
        if (timestampAsDate && finalTimestampCols.includes(colIdx)) {
          return cell;
        }
        const clean = toCellValue(cell);
        return applyValueMapping(
          clean,
          finalColumnKeys[colIdx],
          row,
          valueMappings
        );
      })
    );

    return headers ? [headers, ...formattedData] : formattedData;
  };

  // ---- paginated fetch loop ----
  let pageNumber = 1;
  let safetyCounter = 0;
  while (pageNumber != null) {
    if (++safetyCounter > 1000) break; // guard against bad next_page_number
    const { csvData, nextPageNumber } = await fetchPage(pageNumber, pageSize);

    if (csvData) {
      const parsed = Papa.parse(csvData, {
        header: false,
        skipEmptyLines: true,
      });
      processChunk(parsed, pageNumber === 1);
    }
    pageNumber = nextPageNumber ?? null;
  }

  const finalData = formatAllData();
  if (!finalData || finalData.length <= 1) {
    return { rowCount: 0, fileName: null };
  }

  // ---- build worksheet ----
  const worksheet = XLSX.utils.aoa_to_sheet(finalData, { cellDates: true });

  if (timestampAsDate && finalTimestampCols.length) {
    applyDateFormat(worksheet, finalData, finalTimestampCols, dateFormat);
  }

  styleHeaderRow(worksheet, headers);
  setColumnWidths(worksheet, finalData, headers, dateFormat);
  applyBorderStyle(worksheet, finalData);

  // ---- standardize: AutoFilter dropdowns + frozen header row ----
  if (headers?.length && finalData.length > 1) {
    const ref = `${XLSX.utils.encode_cell({ r: 0, c: 0 })}:${XLSX.utils.encode_cell(
      { r: finalData.length - 1, c: headers.length - 1 }
    )}`;
    // sort/filter ▾ arrows on every header cell
    worksheet['!autofilter'] = { ref };
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  const baseName = generateTimestampedFilename(fileBaseName);
  const fileName = baseName.endsWith('.xlsx')
    ? baseName
    : baseName.replace(/\.csv$/i, '') + '.xlsx';

  XLSX.writeFile(workbook, fileName);

  return { rowCount: finalData.length - 1, fileName };
};
